const content = `
# Validation

Validation measures component model accuracy and simulates how forecast quality affects downstream optimization (Peak optimization Percentage, or PoP). It runs five scenarios of decreasing forecast quality and checks go/no-go criteria.

## Five Validation Scenarios

| Scenario | Name | Description |
|----------|------|-------------|
| **A** | Perfect Foresight | Uses actual building load as the forecast — theoretical upper bound on PoP |
| **B** | Black-Box 10% MAE | Adds AR(1) noise (10% MAE) to actual load — represents a conventional statistical forecast |
| **C** | Cold-Start Component | ComponentForecaster prediction from metadata only — no historical data |
| **D** | 3-Day Fine-Tuned | Component forecast corrected with 72 hours of real meter data |
| **E** | 7-Day Fine-Tuned | Component forecast corrected with 168 hours of real meter data |

Each scenario produces a PoP score by running the forecast through the ADMM orchestrator. Comparing PoP across scenarios shows how much forecast quality matters and how quickly fine-tuning recovers performance.

## Go/No-Go Criteria

Four criteria must pass for a model version to be production-ready:

| Criterion | Threshold | Priority | What It Measures |
|-----------|-----------|----------|-----------------|
| **HVAC MAPE** | < 20% | P0 | Core component prediction accuracy |
| **Cold-Start PoP** | > 85% | P0 | Optimization quality with zero historical data |
| **PoP Improvement** | > 2 pp | P1 | Fine-tuning must improve PoP beyond cold-start |
| **Day-3 PoP** | > 87% | P1 | Performance after just 3 days of real data |

The **Go/No-Go Banner** at the top of the Validation tab shows a green "GO" or red "NO-GO" based on whether all criteria pass.

## Component Accuracy Metrics

Expandable section showing per-component accuracy across test buildings:

| Metric | Description |
|--------|-------------|
| **MAPE** | Mean Absolute Percentage Error — average relative prediction error |
| **R-squared** | Coefficient of determination — how much variance the model explains |
| **MAE** | Mean Absolute Error — average kW prediction error |
| **Peak Error %** | Error in predicting peak hourly demand |

Each component has target thresholds (e.g., HVAC MAPE < 20%, plug loads R-squared > 0.85). The chart uses color coding: green for passing, red for failing.

## Scenario PoP Results

Expandable chart showing PoP scores across all five scenarios. Expected pattern:

- **A** (perfect) sets the ceiling, typically 90-95% PoP
- **B** (black-box) baseline for conventional forecasting
- **C** (cold-start) should achieve > 85% — the primary go/no-go gate
- **D** and **E** should progressively recover toward A, with E typically within 2-3 pp of perfect foresight

## Running Validation

- **Full Validation Suite** — Runs all 5 scenarios on 50 test buildings (takes several minutes)
- **Cold-Start Quick Test** — Runs Scenario C only on 10 buildings (fast sanity check)

Results are stored and listed in Run History. Click any previous run to reload its results.
`;

export default content;
